import type { EncryptedPayload, ReloginSecret } from "./types";

export const PBKDF2_ITERATIONS = 600_000;
const SALT_BYTES = 16;
const IV_BYTES = 12;
const KEY_BITS = 256;

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

async function deriveKey(pin: string, salt: ArrayBuffer, iterations: number): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(pin),
    "PBKDF2",
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt, iterations, hash: "SHA-256" },
    material,
    { name: "AES-GCM", length: KEY_BITS },
    false,
    ["encrypt", "decrypt"],
  );
}

export async function encryptSecret(
  pin: string,
  secret: ReloginSecret,
  iterations: number = PBKDF2_ITERATIONS,
): Promise<EncryptedPayload> {
  const salt = toArrayBuffer(randomBytes(SALT_BYTES));
  const iv = toArrayBuffer(randomBytes(IV_BYTES));
  const key = await deriveKey(pin, salt, iterations);
  const plaintext = new TextEncoder().encode(JSON.stringify(secret));
  const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, plaintext);
  return { salt, iv, iterations, ciphertext };
}

export async function decryptSecret(
  pin: string,
  payload: EncryptedPayload,
): Promise<ReloginSecret | null> {
  try {
    const key = await deriveKey(pin, payload.salt, payload.iterations);
    const plaintext = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: payload.iv },
      key,
      payload.ciphertext,
    );
    const parsed: unknown = JSON.parse(new TextDecoder().decode(plaintext));
    return isReloginSecret(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function isReloginSecret(value: unknown): value is ReloginSecret {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    candidate.kind === "password" &&
    typeof candidate.identifier === "string" &&
    typeof candidate.password === "string" &&
    (candidate.tenantId === null || typeof candidate.tenantId === "string")
  );
}
